import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { moduleEnabled } from "../../../core/blueprint";
import { api } from "../../../core/lib/api";

// Liaison services ↔ procédures (module « guides », cahier §5.7). Aucun import
// entre modules : on parle à GET /api/guides et on dégrade (rien d'affiché)
// quand le module est absent. L'API ne renvoie à un employé que les guides
// publiés ; l'admin voit aussi les brouillons — on ne propose que les publiés.

interface GuideSummary {
  id: string;
  slug: string;
  title: string;
  status: "draft" | "published";
}

export const guidesEnabled = () => moduleEnabled("guides");

/** Liste des guides publiés (slug → titre) ; vide si le module est absent ou en erreur. */
export function useGuides(): { guides: GuideSummary[]; loaded: boolean } {
  const [guides, setGuides] = useState<GuideSummary[]>([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!guidesEnabled()) {
      setLoaded(true);
      return;
    }
    api<GuideSummary[]>("/api/guides")
      .then((list) => setGuides(list.filter((g) => g.status === "published")))
      .catch(() => setGuides([]))
      .finally(() => setLoaded(true));
  }, []);
  return { guides, loaded };
}

/** Cases à cocher des guides publiés (modal de service, admin). */
export function ServiceGuidesPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (slugs: string[]) => void;
}) {
  const { guides, loaded } = useGuides();
  if (!guidesEnabled()) return null;

  const toggle = (slug: string, checked: boolean) => {
    const rest = value.filter((s) => s !== slug);
    onChange(checked ? [...rest, slug] : rest);
  };
  // Un slug lié mais absent de la liste (guide dépublié) reste coché et visible.
  const orphans = value.filter((slug) => !guides.some((g) => g.slug === slug));

  return (
    <fieldset className="atelier-subsection">
      <legend className="atelier-subsection__title">Procédures liées</legend>
      {!loaded ? (
        <p className="atelier-subsection__empty">Chargement…</p>
      ) : guides.length === 0 && orphans.length === 0 ? (
        <p className="atelier-subsection__empty">
          Aucun guide publié : rédigez-en un dans <Link to="/guides">Guides</Link>.
        </p>
      ) : (
        <div className="service-guides__picker">
          {guides.map((g) => (
            <label key={g.slug} className="atelier-check">
              <input
                type="checkbox"
                checked={value.includes(g.slug)}
                onChange={(e) => toggle(g.slug, e.target.checked)}
              />
              {g.title}
            </label>
          ))}
          {orphans.map((slug) => (
            <label key={slug} className="atelier-check service-guides__orphan">
              <input type="checkbox" checked onChange={() => toggle(slug, false)} />
              {slug} (non publié)
            </label>
          ))}
        </div>
      )}
    </fieldset>
  );
}

/** Liens vers les procédures d'une liste de slugs (fiche de travail : ce que le technicien ouvre sur mobile). */
export function ServiceGuidesList({ slugs }: { slugs: string[] }) {
  const { guides, loaded } = useGuides();
  if (!loaded) return <p className="travail__empty">Chargement…</p>;
  const linked = slugs
    .map((slug) => guides.find((g) => g.slug === slug))
    .filter((g): g is GuideSummary => Boolean(g));
  if (linked.length === 0) {
    return <p className="travail__empty">Aucune procédure liée aux services de ce travail.</p>;
  }
  return (
    <ul className="service-guides">
      {linked.map((g) => (
        <li key={g.slug} className="service-guides__item">
          <Link to={`/guides/${g.slug}`} className="service-guides__link">
            {g.title}
          </Link>
        </li>
      ))}
    </ul>
  );
}
